import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import { useE2EAgentAdminRuns, type E2EAgentAdminRun } from "../api/admin";
import { hideChatAndFastTrack } from "../lib/featureFlags";
import DraftRunEditorModal from "../analyst/components/DraftRunEditorModal";
import { useCancelAgentRun } from "../analyst/api/e2e";
import RunTrackIndicator from "../analyst/components/RunTrackIndicator";
import {
  ClickableTableRow,
  ConfirmDialog,
  EditButton,
  IconButton,
  RunStatusTag,
  type RunStatus,
  Search,
  Selector,
  StopIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui";
import { liveElapsed, when } from "../lib/time";
import { PageHeader } from "./WorkspacePrimitives";

function stateKey(run: E2EAgentAdminRun): RunStatus {
  return run.state.kind === "terminal" ? run.state.outcome : run.state.kind;
}

function stateLabel(run: E2EAgentAdminRun): string {
  if (run.state.kind === "draft") return `Draft · ${run.state.input_readiness}`;
  if (run.state.kind === "queued") return "Queued";
  if (run.state.kind === "active") return run.state.phase;
  return run.state.outcome;
}

export default function E2EAgentRunsPage() {
  const restrictedProductionUi = hideChatAndFastTrack();
  const navigate = useNavigate();
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<E2EAgentAdminRun | null>(
    null,
  );
  const cancelRun = useCancelAgentRun();
  const runs = useE2EAgentAdminRuns();
  const [ticker, setTicker] = useState("");
  const [state, setState] = useState("");
  const visible = useMemo(() => {
    const needle = ticker.trim().toUpperCase();
    return (runs.data ?? []).filter(
      (run) =>
        (needle === "" || run.ticker.includes(needle)) &&
        (state === "" || stateKey(run) === state),
    );
  }, [runs.data, state, ticker]);

  return (
    <section
      className="build-ledger-page e2e-admin-page"
      aria-labelledby="e2e-agent-runs-title"
    >
      <PageHeader className="e2e-admin-page-header">
        <div className="build-view-intro">
          <div>
            <span className="section-kicker">Isolated Ryvn execution</span>
            <h2 id="e2e-agent-runs-title">E2E agent fleet</h2>
            <p>
              Durable lifecycle, worker release, sandbox cleanup, and private
              artifact evidence for every standalone model run.
            </p>
          </div>
          <Link className="text-link" to="/new">
            Launch from analyst workspace →
          </Link>
        </div>

        <div className="build-ledger-toolbar" aria-label="E2E agent filters">
          <Selector
            options={[
              { value: "", label: "All lifecycle states" },
              { value: "draft", label: "Draft" },
              { value: "queued", label: "Queued" },
              { value: "active", label: "Active" },
              { value: "succeeded", label: "Succeeded" },
              { value: "failed", label: "Failed" },
              { value: "canceled", label: "Canceled" },
              { value: "expired", label: "Expired" },
            ]}
            value={state}
            onValueChange={setState}
            label="State"
            className="compact-field"
          />
          <Search
            size="sm"
            value={ticker}
            onValueChange={setTicker}
            placeholder="Ticker, e.g. CNK"
            aria-label="Filter E2E agent runs by ticker"
          />
          <span className="build-row-count">{visible.length} rows shown</span>
        </div>
      </PageHeader>

      {runs.isLoading && <div className="state">Loading E2E agent runs…</div>}
      {runs.error && (
        <div className="state err">
          Failed to load runs: {runs.error.message}
        </div>
      )}
      {!runs.isLoading && !runs.error && visible.length === 0 && (
        <div className="build-empty-state">
          <strong>No E2E agent runs match.</strong>
          <span>Clear the filters or launch a new run.</span>
        </div>
      )}
      {visible.length > 0 && (
        <div className="build-table-wrap">
          <Table className="build-ledger-table">
            <TableHead>
              <TableRow>
                <TableHeader>Company</TableHeader>
                <TableHeader>Lifecycle</TableHeader>
                <TableHeader>Hypothesis</TableHeader>
                <TableHeader>Parameters</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Worker / sandbox</TableHeader>
                <TableHeader>Cleanup</TableHeader>
                <TableHeader aria-label="Actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((run) => {
                const attempt = run.attempts.at(-1);
                return (
                  <ClickableTableRow
                    key={run.id}
                    aria-label={`Open ${run.ticker} run`}
                    onActivate={() => navigate(`/admin/runs/${run.id}`)}
                  >
                    <TableCell className="build-company-cell">
                      <div>
                        <strong>{run.ticker}</strong>
                        <span>{run.company_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="agent-run-status-actions">
                        <RunStatusTag status={stateKey(run)}>
                          {stateLabel(run)}
                        </RunStatusTag>
                        <RunTrackIndicator mode={run.parameters.run_mode} />
                        {run.state.kind === "draft" && (
                          <EditButton
                            label={`Edit ${run.ticker} draft`}
                            onClick={() => setEditingDraftId(run.id)}
                          />
                        )}
                      </div>
                      <small className="table-secondary">
                        v{run.state_version} ·{" "}
                        {run.state.kind === "terminal"
                          ? "terminal"
                          : liveElapsed(run.started_at ?? run.created_at)}
                      </small>
                    </TableCell>
                    <TableCell>
                      <span>{run.hypothesis}</span>
                    </TableCell>
                    <TableCell>
                      <strong>{run.parameters.input_mode}</strong>
                      <small className="table-secondary">
                        {run.parameters.model}
                        {!restrictedProductionUi &&
                          ` · ${run.parameters.fast ? "fast" : "standard"}`}
                      </small>
                    </TableCell>
                    <TableCell className="build-table-time">
                      {when(run.created_at)}
                    </TableCell>
                    <TableCell className="build-relation-cell">
                      <code>{attempt?.worker_release ?? "not claimed"}</code>
                      <code>{attempt?.sandbox_pod ?? "no sandbox"}</code>
                    </TableCell>
                    <TableCell>{attempt?.cleanup_status ?? "—"}</TableCell>
                    <TableCell className="build-open-cell">
                      <div className="agent-run-row-actions">
                        {(run.state.kind === "queued" ||
                          run.state.kind === "active") && (
                          <IconButton
                            label={`Cancel ${run.ticker} run`}
                            tooltip="Cancel run — keep in history"
                            icon={<StopIcon />}
                            tone="quiet"
                            disabled={cancelRun.isPending}
                            onClick={() => setPendingCancel(run)}
                          />
                        )}
                      </div>
                    </TableCell>
                  </ClickableTableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {editingDraftId && (
        <DraftRunEditorModal
          draftId={editingDraftId}
          onClose={() => setEditingDraftId(null)}
        />
      )}
      {pendingCancel && (
        <ConfirmDialog
          title={`Cancel ${pendingCancel.ticker} run?`}
          description="The active model process will stop and this run will remain in history as canceled."
          confirmLabel="Cancel run"
          busy={cancelRun.isPending}
          onCancel={() => setPendingCancel(null)}
          onConfirm={() =>
            cancelRun.mutate(pendingCancel.id, {
              onSuccess: () => {
                toast.success("Cancellation requested.");
                setPendingCancel(null);
              },
              onError: (error) => toast.error(error.message),
            })
          }
        />
      )}
    </section>
  );
}
